// @eidollon/contracts/auth
//
// AUTH FLOW CONTRACT — locked Day 0 of Wave 3 so all three lanes can codegen
// against the same shapes from the start.
//
// Implementation notes:
// - The flow is a state machine: login → (password_change?) → (mfa_enroll?) → mfa_verify → access.
// - The server returns a `step` discriminator on every response so the client
//   never has to guess what to do next.
// - Step transitions carry a short-lived (5-minute) `flow_session` token so the
//   client doesn't have to resubmit the password between steps.
// - Errors are GENERIC by design — we never confirm whether an email exists.
//
// Lane A (services/auth) implements these endpoints exactly.
// Lane C (apps/terminal) renders one route per `step` value.

import { z } from 'zod';

/* ──────────────────────────────────────────────────────────────────────────
 *  Primitive types
 * ────────────────────────────────────────────────────────────────────────── */

export const Email = z.string().toLowerCase().trim().email().max(254);
export const TenantRole = z.enum(['tenant_user', 'tenant_admin', 'tenant_billing', 'sys_admin']);

export const ClassificationLevel = z.enum(['PUBLIC', 'CUI', 'SECRET', 'TS', 'TS_SCI']);

/* ──────────────────────────────────────────────────────────────────────────
 *  Step discriminator
 *
 *  After every auth call the server returns one of these `step` values telling
 *  the client what to do next. The terminal SPA has one route per step.
 * ────────────────────────────────────────────────────────────────────────── */

export const AuthStep = z.enum([
  'password_change_required', // user has password_temp=true → must change
  'mfa_enroll_required', // password OK but mfa_enrolled=false → enroll TOTP
  'mfa_required', // all good, just verify a TOTP code
  'authenticated', // tokens issued, redirect to /screen
]);
export type AuthStep = z.infer<typeof AuthStep>;

/* ──────────────────────────────────────────────────────────────────────────
 *  POST /v1/auth/login
 * ────────────────────────────────────────────────────────────────────────── */

export const LoginRequest = z.object({
  email: Email,
  password: z.string().min(1).max(256),
});

export const FlowSession = z.string().min(64).max(2048); // signed JWT, 5min TTL

export const LoginResponse = z.discriminatedUnion('step', [
  z.object({ step: z.literal('password_change_required'), flow_session: FlowSession }),
  z.object({ step: z.literal('mfa_enroll_required'), flow_session: FlowSession }),
  z.object({ step: z.literal('mfa_required'), flow_session: FlowSession }),
]);
export type LoginRequest = z.infer<typeof LoginRequest>;
export type LoginResponse = z.infer<typeof LoginResponse>;

/* ──────────────────────────────────────────────────────────────────────────
 *  POST /v1/auth/password/change
 *
 *  Used both for first-login temp-password change AND for in-app change.
 *  When called from /account, the caller has a valid access token → no flow_session.
 * ────────────────────────────────────────────────────────────────────────── */

export const PasswordChangeRequest = z.object({
  flow_session: FlowSession.optional(), // present during login flow
  current_password: z.string().min(1).max(256).optional(), // present when changing in-app
  new_password: z.string().min(14).max(256), // zxcvbn ≥3 enforced server-side
});

export const PasswordChangeResponse = z.object({
  step: AuthStep, // typically mfa_enroll_required after first change
  flow_session: FlowSession.optional(), // present if flow continues
});

/* ──────────────────────────────────────────────────────────────────────────
 *  POST /v1/auth/mfa/enroll/start
 * ────────────────────────────────────────────────────────────────────────── */

export const MfaEnrollStartRequest = z.object({
  flow_session: FlowSession,
});

export const MfaEnrollStartResponse = z.object({
  // The TOTP secret in base32, for users typing it manually instead of QR.
  secret_base32: z.string().min(16).max(64),
  // otpauth:// URI; the client renders this as a QR code.
  otpauth_uri: z.string().regex(/^otpauth:\/\/totp\//),
  // Embedded QR rendered as inline SVG (server-generated; client just dangerouslySetsInnerHTML).
  qr_svg: z.string(),
  // Same flow_session echoed back so the client doesn't lose the chain.
  flow_session: FlowSession,
});

/* ──────────────────────────────────────────────────────────────────────────
 *  POST /v1/auth/mfa/enroll/confirm
 * ────────────────────────────────────────────────────────────────────────── */

export const MfaEnrollConfirmRequest = z.object({
  flow_session: FlowSession,
  totp_code: z.string().regex(/^\d{6}$/),
});

export const MfaEnrollConfirmResponse = z.object({
  step: z.literal('mfa_required'),
  flow_session: FlowSession,
  // 10 single-use recovery codes — shown ONCE to the user.
  recovery_codes: z.array(z.string().regex(/^[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}$/)).length(10),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  POST /v1/auth/mfa/verify
 * ────────────────────────────────────────────────────────────────────────── */

export const MfaVerifyRequest = z
  .object({
    flow_session: FlowSession,
    totp_code: z
      .string()
      .regex(/^\d{6}$/)
      .optional(),
    recovery_code: z
      .string()
      .regex(/^[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}$/)
      .optional(),
  })
  .refine((v) => Boolean(v.totp_code) !== Boolean(v.recovery_code), {
    message: 'exactly one of totp_code or recovery_code is required',
  });

export const Me = z.object({
  user_id: z.string().uuid(),
  email: Email,
  display_name: z.string().nullable(),
  tenant_id: z.string().uuid(),
  tenant_name: z.string(),
  role: TenantRole,
  classification: ClassificationLevel, // tenant default classification
  mfa_enrolled: z.literal(true),
  password_temp: z.literal(false),
});
export type Me = z.infer<typeof Me>;

export const MfaVerifyResponse = z.object({
  step: z.literal('authenticated'),
  access_token: z.string(), // JWT, 15min TTL, returned in body
  // refresh_token comes via Set-Cookie: __Host-rt only — never in JSON
  expires_in: z.number().int(), // seconds
  me: Me,
});

/* ──────────────────────────────────────────────────────────────────────────
 *  POST /v1/auth/refresh
 *
 *  Reads __Host-rt cookie (HttpOnly, Secure, SameSite=Strict).
 *  Sets a NEW __Host-rt cookie on response and returns a new access token.
 *  Reuse detection: presenting a revoked refresh token revokes ALL sessions.
 * ────────────────────────────────────────────────────────────────────────── */

export const RefreshResponse = z.object({
  access_token: z.string(),
  expires_in: z.number().int(),
  me: Me,
});

/* ──────────────────────────────────────────────────────────────────────────
 *  GET /v1/auth/sessions  &  POST /v1/auth/sessions/:id/revoke
 * ────────────────────────────────────────────────────────────────────────── */

export const SessionRow = z.object({
  id: z.string().uuid(),
  ip: z.string().nullable(),
  user_agent: z.string().nullable(),
  created_at: z.string(), // ISO timestamp
  last_used_at: z.string().nullable(),
  expires_at: z.string(),
  current: z.boolean(), // true if this is the calling session
});
export const SessionsResponse = z.object({
  sessions: z.array(SessionRow),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  Generic error envelope
 *
 *  All errors return THIS shape. We never leak whether an email exists, never
 *  enumerate which step failed, never echo back the user's input.
 * ────────────────────────────────────────────────────────────────────────── */

export const ErrorCode = z.enum([
  'invalid_credentials',
  'account_locked',
  'rate_limited',
  'flow_expired',
  'weak_password',
  'invalid_totp',
  'invalid_recovery_code',
  'session_expired',
  'session_revoked',
  'invalid_request',
  'internal_error',
]);

export const ErrorResponse = z.object({
  error: ErrorCode,
  // request_id always present so users can quote it to support
  request_id: z.string().uuid(),
  // retry_after is present ONLY for rate_limited / account_locked
  retry_after_seconds: z.number().int().nonnegative().optional(),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  Cookie + token conventions (documented for both lanes)
 * ────────────────────────────────────────────────────────────────────────── */

export const REFRESH_COOKIE_NAME = '__Host-rt';
export const ACCESS_TOKEN_TTL_SEC = 15 * 60; // 15 minutes
export const REFRESH_TOKEN_TTL_SEC = 30 * 24 * 60 * 60; // 30 days
export const FLOW_SESSION_TTL_SEC = 5 * 60; // 5 minutes

/** JWT claims shape for the access token (signed by services/auth, validated
 *  by services/api on every request). */
export const AccessTokenClaims = z.object({
  sub: z.string().uuid(), // user_id
  tid: z.string().uuid(), // tenant_id
  role: TenantRole,
  jti: z.string().uuid(), // unique token id
  iat: z.number().int(), // issued at (unix sec)
  exp: z.number().int(), // expires at
  iph: z.string().min(1), // ip_hash (binding)
  uah: z.string().min(1), // ua_hash (binding)
});
export type AccessTokenClaims = z.infer<typeof AccessTokenClaims>;
